import { useEffect, useRef, useState } from 'react';
import { checkLinkWithNetwork, retireLink, replaceLink } from '../models/paymentsV2Model';
import { usePaymentCommand } from './usePaymentCommand';
import { paymentErrorCode } from '../utilities/paymentErrors';
import { canRetireLink, latestLinkCheck, REPLACEMENT_COPY } from '../utilities/linkLifecycle';

export function useLinkLifecycle(intent, reload, hasReceipt = false) {
  const [sessionCheck, setCheck] = useState(null), [checking, setChecking] = useState(false);
  const check = latestLinkCheck(intent, sessionCheck);
  const [checkError, setCheckError] = useState(null), [timedOut, setTimedOut] = useState(false);
  const [, tick] = useState(0);
  const flight = useRef(false), generation = useRef(0), poll = useRef(null), deadline = useRef(null);
  const reloadRef = useRef(reload); reloadRef.current = reload;
  const pendingMode = useRef(null);
  const command = usePaymentCommand(`link-lifecycle-${intent?.id}`, () => reloadRef.current({ silent: true }));
  useEffect(() => {
    return () => { generation.current += 1; clearTimeout(poll.current); clearTimeout(deadline.current); };
  }, []);
  useEffect(() => {
    const remaining = Date.parse(check?.completed_at) + 300001 - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return undefined;
    const timer = setTimeout(() => tick(value => value + 1), remaining);
    return () => clearTimeout(timer);
  }, [check]);
  const checkNow = async () => {
    if (flight.current) return;
    flight.current = true; setChecking(true); setCheckError(null); setTimedOut(false);
    const id = ++generation.current;
    const stop = timeout => {
      if (id !== generation.current) return;
      generation.current += 1; clearTimeout(poll.current); clearTimeout(deadline.current);
      flight.current = false; setChecking(false); if (timeout) setTimedOut(true);
    };
    deadline.current = setTimeout(() => stop(true), 60000);
    const read = async () => {
      try {
        const result = await checkLinkWithNetwork(intent.id);
        if (generation.current !== id) return;
        setCheck(result.check ? { ...result.check, intent_id: intent.id } : null);
        if (result.httpStatus === 202 || result.check?.status === 'checking') poll.current = setTimeout(read, 5000);
        else { stop(false); reloadRef.current({ silent: true }).catch(() => {}); }
      } catch (error) { if (generation.current === id) { setCheckError(error); stop(false); } }
    };
    await read();
  };
  const submit = async (mode, values) => {
    if (!command.frozen) pendingMode.current = mode;
    const result = await command.submit(payload => (mode === 'replace' ? replaceLink : retireLink)(intent.id, payload), {
      expected_version: intent.version, reason_code: values.reason_code, note: values.note || null,
    });
    return result;
  };
  const code = paymentErrorCode(command.error);
  useEffect(() => { if (code === 'PAYMENT_LINK_VERSION_CONFLICT') reloadRef.current({ silent: true }).catch(() => {}); }, [code]);
  return { ...command, submit, checkNow, check, checking, checkError, timedOut,
    pendingMode: pendingMode.current,
    eligible: !hasReceipt && canRetireLink(intent, check),
    notice: REPLACEMENT_COPY[command.result?.next_action] || (command.result ? 'Recorded. The replacement, if any, has not been sent to the customer.' : null),
  };
}
